import { A11yModule } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ClickOutsideDirectiveModule } from '@commons/directives/click-outside/click-outside.directive';
import { PressEscapeKeyDirectiveModule } from '@commons/directives/press-escape-key/press-escape-key.directive';
import { SearchEngineFieldSelectWeeklyDuration } from '@domains/search-engine/entities/select-weekly-duration-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SharedModule } from '@shared/shared.module';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { BehaviorSubject } from 'rxjs';

interface WeeklyDurationValue {
  durationSelectFrom: number;
  durationSelectTo: number;
}

export interface WeeklyDurationConfig {
  maxHour: number;
}

@UntilDestroy()
@Component({
  selector: 'app-select-weekly-duration',
  templateUrl: './select-weekly-duration.component.html',
  styleUrls: ['./select-weekly-duration.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectWeeklyDurationComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectWeeklyDurationComponent implements ControlValueAccessor, OnInit, OnDestroy {
  options = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
  durationForm: FormGroup;

  fromOptions$ = new BehaviorSubject<number[]>(this.options);
  toOptions$ = new BehaviorSubject<number[]>(this.options);

  @Input() field!: SearchEngineFieldSelectWeeklyDuration;
  private _value!: WeeklyDurationValue;

  set value(value: WeeklyDurationValue) {
    this._value = value;
    this.onChanged(this._value);
    this.onTouched();
  }

  get value(): WeeklyDurationValue {
    return this._value;
  }

  get durationSelectFrom(): FormControl {
    return this.durationForm.get('durationSelectFrom') as FormControl;
  }

  get durationSelectTo(): FormControl {
    return this.durationForm.get('durationSelectTo') as FormControl;
  }

  constructor(private readonly cdr: ChangeDetectorRef, private readonly fb: FormBuilder) {
    this.durationForm = this.fb.group({
      durationSelectFrom: undefined,
      durationSelectTo: undefined,
    });
  }

  ngOnInit(): void {
    if (this.field.maxHour) {
      this.options = this.populateOptions(this.field.maxHour);
    }

    this.durationForm.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (val) => {
        this.value = val;
      },
    });

    this.bindOptionsChanges();
  }

  private populateOptions(maxLenght: number) {
    return [...Array(maxLenght + 1).keys()];
  }

  clearValueSelectFrom(event: any): void {
    event.stopPropagation();
    this.durationSelectFrom.setValue(undefined);
  }

  clearValueSelectTo(event: any): void {
    event.stopPropagation();
    this.durationSelectTo.setValue(undefined);
  }

  bindOptionsChanges(): void {
    this.durationSelectTo.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (value) => {
        if (value) {
          this.fromOptions$.next(this.options.slice(0, value + 1));
        } else {
          this.fromOptions$.next(this.options);
        }
      },
    });

    this.durationSelectFrom.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (value) => {
        if (value) {
          this.toOptions$.next(this.options.slice(value, this.options.length));
        } else {
          this.toOptions$.next(this.options);
        }
      },
    });
  }

  onChanged: (_: WeeklyDurationValue) => void = (_: WeeklyDurationValue) => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  writeValue(val: WeeklyDurationValue): void {
    if (val) {
      this.durationForm.setValue(val);
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = (value) => {
      fn(value);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  trackByOption(index: number, _: number): number {
    return index;
  }

  ngOnDestroy(): void {
    this.fromOptions$.complete();
    this.toOptions$.complete();
  }
}

@NgModule({
  imports: [
    SharedModule,
    MatSelectModule,
    MatIconModule,
    MatInputModule,
    ClickOutsideDirectiveModule,
    NgUpperFirstPipeModule,
    A11yModule,
    PressEscapeKeyDirectiveModule,
    ReactiveFormsModule,
  ],
  declarations: [SelectWeeklyDurationComponent],
  exports: [SelectWeeklyDurationComponent],
})
export class SelectWeeklyDurationModule {}
